import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, TextField, Grid, Paper } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { saveAs } from 'file-saver';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import axios from '../../axiosInstance';
import dayjs from 'dayjs';
import Profile from '../../components/Profile';
import Notification from '../../components/Notification';

const UsageReports = () => {
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalBooks, setTotalBooks] = useState(0);
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [usageData, setUsageData] = useState([]);

  const fetchSummary = async () => {
    try {
      const res = await axios.get('https://backend.scholarkart.in/api/usage/summary');
      setTotalUsers(res.data.totalUsers || 0);
      setTotalBooks(res.data.totalBooks || 0);
    } catch (error) {
      console.error('Error fetching usage summary:', error);
    }
  };

  const fetchUsageDetails = async () => {
    try {
      const res = await axios.get('https://backend.scholarkart.in/api/usage/details', {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        }
      });
      setUsageData(res.data.logs || []);
    } catch (error) {
      console.error('Error fetching usage details:', error);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchUsageDetails();
  }, [startDate, endDate]);

  const handleDownloadUsage = async () => {
    try {
      const res = await axios.get('https://backend.scholarkart.in/api/usage/details', {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        }
      });

      const logs = res.data.logs || [];

      const headers = ['Username', 'Login Time', 'Accessed Book Title', 'Chapter Title', 'Access Time'];
      const rows = logs.map(log => [
        log.username,
        log.loginTime ? dayjs(log.loginTime).format('YYYY-MM-DD HH:mm') : '',
        log.bookTitle || '',
        log.chapterTitle || '',
        log.accessTime ? dayjs(log.accessTime).format('YYYY-MM-DD HH:mm') : ''
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(","))
      ].join("\n");

      const filename = `usage_report_${startDate.format('DDMMMYYYY')}_to_${endDate.format('DDMMMYYYY')}.csv`;

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading usage report:', error);
    }
  };

  const processGraphData = () => {
    const graphData = {};

    usageData.forEach(log => {
      const dateKey = dayjs(log.loginTime || log.accessTime).format('YYYY-MM-DD');
      if (!graphData[dateKey]) {
        graphData[dateKey] = { date: dateKey, logins: 0, accesses: 0 };
      }

      if (log.loginTime) graphData[dateKey].logins += 1;
      if (log.accessTime) graphData[dateKey].accesses += 1;
    });

    return Object.values(graphData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const processTopBooks = () => {
    const bookMap = {};
    usageData.forEach(log => {
      if (log.bookTitle) {
        bookMap[log.bookTitle] = (bookMap[log.bookTitle] || 0) + 1;
      }
    });
    return Object.entries(bookMap)
      .map(([bookTitle, count]) => ({ bookTitle, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const processTopUsers = () => {
    const userMap = {};
    usageData.forEach(log => {
      if (log.username) {
        userMap[log.username] = (userMap[log.username] || 0) + 1;
      }
    });
    return Object.entries(userMap)
      .map(([username, count]) => ({ username, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const processTopChapters = () => {
    const chapterMap = {};
    usageData.forEach(log => {
      if (log.chapterTitle) {
        chapterMap[log.chapterTitle] = (chapterMap[log.chapterTitle] || 0) + 1;
      }
    });
    return Object.entries(chapterMap)
      .map(([chapterTitle, count]) => ({ chapterTitle, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };
  
  const topChapters = processTopChapters();

  const graphData = processGraphData();
  const topBooks = processTopBooks();
  const topUsers = processTopUsers();

  return (
    <main className="flex-grow p-1">
    <div className="flex justify-between items-center relative bg-white p-4 rounded-lg shadow-md">

        <div className="relative w-full max-w-lg">

        </div>

      <div className="flex items-center space-x-4 relative">
        <Notification />
        <Profile />
      </div>
    </div>

      <Box p={4}>
        <Typography variant="h4" fontWeight="bold" mb={4}>Usage Reports</Typography>

        <Grid container spacing={4} mb={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ padding: 3 }}>
              <Typography variant="h6" color="primary">Total Users</Typography>
              <Typography variant="h4" fontWeight="bold">{totalUsers}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ padding: 3 }}>
              <Typography variant="h6" color="primary">Total Books</Typography>
              <Typography variant="h4" fontWeight="bold">{totalBooks}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ padding: 3, marginBottom: 4 }}>
          <Typography variant="h6" mb={2}>Select Date Range to Download Report</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
          </LocalizationProvider>

          <Button variant="contained" color="primary" onClick={handleDownloadUsage}>
            Download Usage Report
          </Button>
        </Paper>

        <Typography variant="h5" mt={4} mb={2}>Top 5 Accessed Books</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topBooks} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="bookTitle" type="category" width={200} />
              <Tooltip />
              <Bar dataKey="count" fill="#8884d8" />
            </BarChart>
          </ResponsiveContainer>

          <Typography variant="h5" mt={4} mb={2}>Top 5 Accessed Chapters</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topChapters} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="chapterTitle" type="category" width={200} />
                <Tooltip />
                <Bar dataKey="count" fill="#6c5ce7" />
              </BarChart>
            </ResponsiveContainer>


          <Typography variant="h5" mt={4} mb={2}>Top 5 Active Users</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topUsers} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="username" type="category" width={150} />
              <Tooltip />
              <Bar dataKey="count" fill="#ffc658" />
            </BarChart>
          </ResponsiveContainer>


        <Typography variant="h5" mb={2}>Login Activities (Line Chart)</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="logins" stroke="#1976d2" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>

        <Typography variant="h5" mt={4} mb={2}>Book Access Activities (Bar Chart)</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="accesses" fill="#82ca9d" />
          </BarChart>
        </ResponsiveContainer>



      </Box>
    </main>
  );
};

export default UsageReports;
